import {
  AddOperation,
  Operation,
  OperationSpec,
  OperationState,
  OperationType,
} from '@sqior/js/operation';
import { StateOverlay } from '@sqior/js/state';
import { joinPath } from '@sqior/js/url';
import { EntityHeader } from '@sqior/js/entity';
import { ValueObject, isEqual } from '@sqior/js/data';
//import { Logger } from "@sqior/js/log";

export const MessengerTabPath = 'messenger-tab';

export enum MessengerPages {
  CommandHistory = 'page.command-history',
  Settings = 'page.settings',
  ChoseRoom = 'page.chose-room',
  ChoseSpecialty = 'page.chose-specialty',
  QRScan = 'page.qr-scan',
}

/** Main Messenger Tabs */
export enum MessengerTab {
  None = 'none',
  Todo = 'todo',
  List = 'list',
  Conversation = 'conversation',
  Menu = 'menu',
  Analytics = 'analytics',
}
/** Model of pages which can be opened on top of each other */
export type MessengerTabPageSpec = EntityHeader & {
  data?: ValueObject;
  lastChanged?: number;
};

/** Items which are allowed to be stackable */
export type MessengerTabStackItem = MessengerTab | MessengerTabPageSpec;
/** The stack itself */
export type MessengerTabStack = MessengerTabStackItem[];

/** Selects a message tab
 * (potentially stacked tabs are removed)
 */
export function SelectMessengerTab(tab: MessengerTab): OperationSpec<SelectMessengerTabData> {
  return { type: OperationType.Add, path: SelectMessengerTabPath, data: { tab: tab } };
}
export const SelectMessengerTabPath = joinPath(MessengerTabPath, 'select');
export type SelectMessengerTabData = { tab: MessengerTab };

/** Opens a page on top of the current tab stack  */
export enum OpenPageReplaceStrategy {
  IfEqual = 'if-equal', // Replace a potentially open page if provided page is equal (all attributes)
  SamePageType = 'same-page-type', // Replace a potentially open page if provided page type is equal (entityType)
  Always = 'always', // Always replace a potentially open page
  ClearStack = 'clear-stack', // Clear all pages except the level 0
}
export function OpenPage(
  page: MessengerTabStackItem,
  replaceStrategy?: OpenPageReplaceStrategy
): OperationSpec<OpenPageData> {
  const res: OperationSpec<OpenPageData> = {
    type: OperationType.Add,
    path: OpenPagePath,
    data: { page: page },
  };
  if (replaceStrategy) res.data.replaceStrategy = replaceStrategy;
  return res;
}
export const OpenPagePath = joinPath(MessengerTabPath, 'open');
export type OpenPageData = {
  page: MessengerTabStackItem;
  replaceStrategy?: OpenPageReplaceStrategy;
};

/** Closes the most top page from the current tab stack */

export type ClosePageData = { page?: MessengerTabPageSpec };
export function ClosePage(page?: MessengerTabPageSpec): OperationSpec<ClosePageData> {
  console.log('MessengerTabOverlay - ClosePage');
  const pageData: ClosePageData = {};
  if (page) pageData.page = page;
  return { type: OperationType.Add, path: CloseViewPath, data: pageData };
}
export const CloseViewPath = joinPath(MessengerTabPath, 'close');

export function MessengerTabOverlay(op: Operation, path: string, curVal: ValueObject | undefined) {
  if (!(op instanceof AddOperation)) return undefined;

  const oldValue = (curVal as unknown as MessengerTabStack) || [];
  const overlay = StateOverlay.create<MessengerTabStack>(
    (value, timestamp) => {
      timestamp;

      let expectedValue: MessengerTabStack = oldValue;
      if (path === 'select') {
        const data = op.data as SelectMessengerTabData;
        expectedValue = [data.tab];
      } else if (path === 'open') {
        const data = op.data as OpenPageData;
        expectedValue = [...expectedValue, data.page];
      } else if (path === 'close') {
        expectedValue = value.slice(0, expectedValue.length - 1);
      }

      //Logger.debug(['MessengerTabOverlay.create', JSON.stringify(oldValue), JSON.stringify(value), JSON.stringify(timestamp), JSON.stringify(op), path, expectedValue])

      if (isEqual(value, expectedValue))
        setTimeout(() => {
          overlay.clear();
        });

      return expectedValue;
    },
    [MessengerTab.Todo]
  );

  /* Clear overlay if operation failed */
  op.stateChange.on((state) => {
    if (state === OperationState.Failed) {
      //Logger.debug(['MessengerTabOverlay.op.stateChange-failed', JSON.stringify(oldValue), state, JSON.stringify(op), path])
      overlay.clear();
    } else if (state === OperationState.Completed) {
      //Logger.debug(['MessengerTabOverlay.op.stateChange-completed', JSON.stringify(oldValue), state, JSON.stringify(op), path])
      setTimeout(() => {
        overlay.clear();
      }, 100);
    }
  });

  return overlay;
}
